import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { AgentMessageFile } from 'AgentApp/ts/interfaces/agent-message.interface';

import { AgentPromptListService } from 'AgentApp/ts/features/agent-prompt-list/services/agent-prompt-list.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { UtilsService } from 'Angular/services/core/utils.service';

import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';

import { getFileIcon } from 'AgentApp/ts/utils/agent-file';
import { stripContextBlocks } from 'AgentApp/ts/utils/context-block';

@Component({
    selector: 'ycn-agent-user-message',
    imports: [IconModule, TooltipModule],
    templateUrl: './agent-user-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentUserMessageComponent {
    private readonly agentPromptListService = inject(AgentPromptListService);

    private readonly utilsService = inject(UtilsService);

    private readonly urlsService = inject(UrlsService);

    protected readonly messageText = input.required<string>();

    protected readonly displayText = computed(() => stripContextBlocks(this.messageText()));

    protected readonly files = input<AgentMessageFile[]>([]);

    protected readonly savePromptEnabled = input<boolean>(true);

    protected readonly filesWithFileName = computed(() => {
        const files = this.files();
        return files.map((file) => {
            const fileName = this.getFileName(file);
            return { ...file, fileName, icon: getFileIcon(fileName, file.attachment_mime_type) };
        });
    });

    protected readonly promptLibraryClicked = output<Event>();

    protected readonly promptLibraryUrl = this.urlsService.getUrl(this.urlsService.AGENT.PAGE, undefined, {
        view: 'savedPrompts',
    });

    protected readonly saving = signal(false);

    // Flipped once the user saves this message this session; renders the filled bookmark.
    protected readonly saved = signal(false);

    // Transient checkmark shown for ~2s right after saving, before settling on the filled bookmark.
    protected readonly showSavedCheck = signal(false);

    protected readonly copied = signal(false);

    getFileName(file: AgentMessageFile): string {
        return file.attachment_url.split('/').pop() || file.attachment_url;
    }

    savePrompt(): void {
        if (this.saving() || this.saved()) {
            return;
        }
        this.saving.set(true);
        this.agentPromptListService.createAgentPrompt({ prompt: this.displayText() }).subscribe({
            next: () => {
                this.saving.set(false);
                this.saved.set(true);
                this.showSavedCheck.set(true);
                setTimeout(() => {
                    this.showSavedCheck.set(false);
                }, 2000);
            },
            error: () => {
                this.saving.set(false);
            },
        });
    }

    copyText(): void {
        const contentCopied = this.utilsService.copyToClipboard(this.displayText());
        if (contentCopied && !this.copied()) {
            this.copied.set(true);
            setTimeout(() => {
                this.copied.set(false);
            }, 1300);
        }
    }
}
